import React, { useEffect, useRef, useState } from 'react';
import { Product } from '../_types';
import Benefits from './benefits/benefits';
import styles from './choose-premium-product.module.scss';
import { PageTitle } from 'components/page-title';
import { Language } from '_types/language';
import { useSelector } from 'react-redux';
import { deviceSelector, languageSelector } from 'store-modules/app-settings';
import {
    returnCopyForLanguage,
    returnRoutesForLanguage,
} from 'services/language-service';
import { routeObj } from 'pages/account-page/account-home/account-home';
import { Dots } from 'components/loading/dots';
import applePayClient from 'api/apple-pay-client';
import { useNavigate } from 'react-router-dom';
import { Link } from 'components/link';
import { PurchaseService } from '../../../common-logic/purchase-service';
import { IconButton } from 'components/base';
import { useTranslation } from 'react-i18next';
import { ProductSelect } from './product-select';

type Props = {
    products: Product[];
    onProductSelected: (product?: Product) => void;
    moveToRegistrationByEmail: (enterProCode: boolean) => void;
};
interface ViewCopy {
    title: string;
    description: string;
    description2: string;
    benefits: string[];
    linkPrivacy: string;
    linkTnc: string;
    tnc: string;
}

const nbCopy: ViewCopy = {
    title: 'Velg din FuelPlan',
    description: 'Prøv 7 dager gratis.',
    description2: 'Avslutt når som helst.',
    benefits: [
        'Ubegrenset tilgang til alle spørsmål og kategorier.',
        'Samtaler for alle årstider og begivenheter.',
        'Lagre favorittspørsmål og del dem med venner.',
        'Bygg bedre team og sterkere relasjoner både på jobb og hjemme.',
        'Et verktøy for å skape meningsfulle samtaler i alle anledninger.',
    ],
    linkPrivacy: 'Personvern',
    linkTnc: 'Vilkår for bruk',
    tnc: 'Abonnementsvilkår: Etter prøveperioden fornyes FuelBox sitt årsabonnement automatisk til 599,- NOK, med mindre det avsluttes i kontoinnstillinger minst 24 timer før gjeldende periode utløper.',
};

const enCopy: ViewCopy = {
    title: 'Choose your FuelPlan',
    description: 'Try 7 days free.',
    description2: 'Cancel anytime.',
    benefits: [
        'Unlimited access to all questions and categories.',
        'Conversations for all seasons and events.',
        'Save favourites and track your conversations.',
        'Reflect daily with thoughtful prompts designed for personal growth.',
        'Ad-free, distraction-free space for real conversations.',
    ],
    linkPrivacy: 'Privacy policy',
    linkTnc: 'Terms of use',
    tnc: 'Subscription terms: After free trial, FuelBox yearly subscription is $59,99, automatically renews unless turned off in Account Settings at least 24h before current period ends.',
};

const copies: { name: Language; copy: ViewCopy }[] = [
    {
        name: 'nb',
        copy: nbCopy,
    },
    {
        name: 'en',
        copy: enCopy,
    },
];

const BASE_URL = '/wp-json/fuelbox/v1';
const { REACT_APP_API_URL } = process?.env;

const ChoosePremProd: React.FC<Props> = ({ products, onProductSelected }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const language = useSelector(languageSelector);
    const device = useSelector(deviceSelector);

    const ref = useRef<HTMLDivElement>(null);

    const routes = returnRoutesForLanguage(language, routeObj);

    const { title, description, description2, benefits, linkPrivacy, linkTnc } =
        returnCopyForLanguage(language, copies);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.HTMLElement.prototype.scrollIntoView = function () {};
    useEffect(() => {
        !!ref &&
            !!ref.current &&
            ref.current.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const SubscriptionNotice = () => {
        return (
            <div
                style={{
                    fontSize: '11px',
                    maxWidth: '400px',
                    textAlign: 'center',
                    padding: '1rem',
                    marginTop: '48px',
                }}
            >
                <p>
                    Abonnementsvilkår: Etter prøveperioden fornyes FuelBox sitt
                    årsabonnement automatisk til 599,- NOK, med mindre det
                    avsluttes i kontoinnstillinger minst 24 timer før gjeldende
                    periode utløper.
                </p>
                <p>
                    <Link className={styles.link} to={routes.privacyTerms}>
                        {linkPrivacy}
                    </Link>{' '}
                    &{' '}
                    <Link className={styles.link} to={routes.tnc}>
                        {linkTnc}
                    </Link>
                </p>
            </div>
        );
    };

    const onClick = (): void => {
        const purchaseService = new PurchaseService();
        const IAP2 = purchaseService.store!;
        setLoading(true);

        const productId = products[0].id;

        applePayClient
            .createOrder({ productId })
            .then(({ order_id }) => {
                IAP2.validator = `${REACT_APP_API_URL}${BASE_URL}/AppleVerifyReceipt?order=${order_id}`;

                IAP2.when()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .approved(function (p: any) {
                        p.verify();
                    })
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .verified(function (p: any) {
                        p.finish();
                        navigate(routes.account.index);
                    })
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .unverified((p: any) => {
                        console.error(p);
                        setLoading(false);
                        setIsError(true);
                    });

                IAP2.error((err) => {
                    setLoading(false);
                    setIsError(true);
                    console.error(err);
                });
                IAP2.restorePurchases();
            })
            .catch((err) => {
                console.error(err);
                throw err;
            });
    };

    return (
        <article
            data-testid='component-choose-sub-plan'
            className={styles.ChoosePremProd}
        >
            {!!products.length && (
                <>
                    <IconButton
                        aria-label={t('close')}
                        className={styles.closeButton}
                        icon='close'
                        onClick={() => navigate(routes.account.subscription)}
                    />
                    <PageTitle className={styles.title} title={title} />
                    <p className={styles.Description}>{description}</p>
                    <p className={styles.Description2}>{description2}</p>
                    <section
                        className={styles.Benefits}
                        aria-labelledby='Benefits'
                    >
                        <Benefits benefits={benefits} />
                    </section>
                    <section className={styles.Plans}>
                        <ProductSelect
                            products={products}
                            productSelectHandler={onProductSelected}
                            navigateToCompanySignup={() =>
                                navigate(routes.companySignupPage)
                            }
                            language={language}
                        />
                        {device === 'ios' ||
                            (true &&
                                (loading ? (
                                    <section className={styles.dots}>
                                        <div>
                                            <Dots />
                                        </div>
                                    </section>
                                ) : (
                                    <section
                                        className={styles.restorePurchases}
                                    >
                                        {isError ? (
                                            <p>Error Verifying Purchase</p>
                                        ) : (
                                            <a
                                                href='#restore-purchases'
                                                onClick={onClick}
                                            >
                                                Restore Purchases
                                            </a>
                                        )}
                                    </section>
                                )))}
                        <SubscriptionNotice />
                    </section>
                    <div ref={ref} />
                </>
            )}
        </article>
    );
};

export default React.memo(ChoosePremProd);
